import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  MapPin, 
  RefreshCw,
  Database,
  Filter,
  Download,
  Settings,
  TrendingUp,
  Calculator
} from "lucide-react";
import { 
  RegionalPriceFactor
} from "@/types";

const RegionalFactors = () => {
  const [selectedQuarter, setSelectedQuarter] = useState("2024-Q1");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedGeoZone, setSelectedGeoZone] = useState("all");

  const [regionalFactors] = useState<RegionalPriceFactor[]>([
    {
      id: "1",
      geoZoneId: "LAG",
      materialCategory: "Cement",
      quarter: "2024-Q1",
      year: 2024,
      quarterNumber: 1,
      baseFactor: 1.35,
      transportFactor: 1.25,
      laborFactor: 1.40,
      overheadFactor: 1.45,
      marketFactor: 1.30,
      totalFactor: 3.45,
      inflationRate: 16.2,
      notes: "New year, increased costs",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    },
    {
      id: "2",
      geoZoneId: "ABJ",
      materialCategory: "Cement",
      quarter: "2024-Q1",
      year: 2024,
      quarterNumber: 1,
      baseFactor: 1.20,
      transportFactor: 1.15,
      laborFactor: 1.25,
      overheadFactor: 1.30,
      marketFactor: 1.20,
      totalFactor: 2.90,
      inflationRate: 15.8,
      notes: "Capital city pricing",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    },
    {
      id: "3",
      geoZoneId: "KAN",
      materialCategory: "Cement",
      quarter: "2024-Q1",
      year: 2024,
      quarterNumber: 1,
      baseFactor: 1.10,
      transportFactor: 1.05,
      laborFactor: 1.15,
      overheadFactor: 1.20,
      marketFactor: 1.10,
      totalFactor: 2.50,
      inflationRate: 14.5,
      notes: "Northern region pricing",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    },
    {
      id: "4",
      geoZoneId: "LAG",
      materialCategory: "Steel",
      quarter: "2024-Q1",
      year: 2024,
      quarterNumber: 1,
      baseFactor: 1.40,
      transportFactor: 1.30,
      laborFactor: 1.45,
      overheadFactor: 1.50,
      marketFactor: 1.35,
      totalFactor: 3.60,
      inflationRate: 16.2,
      notes: "Steel pricing factors",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    },
    {
      id: "5",
      geoZoneId: "ABJ",
      materialCategory: "Steel",
      quarter: "2024-Q1",
      year: 2024,
      quarterNumber: 1,
      baseFactor: 1.25,
      transportFactor: 1.20,
      laborFactor: 1.30,
      overheadFactor: 1.35,
      marketFactor: 1.25,
      totalFactor: 3.05,
      inflationRate: 15.8,
      notes: "Steel pricing factors",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    },
    {
      id: "6",
      geoZoneId: "PHC",
      materialCategory: "Cement",
      quarter: "2024-Q1",
      year: 2024,
      quarterNumber: 1,
      baseFactor: 1.15,
      transportFactor: 1.10,
      laborFactor: 1.20,
      overheadFactor: 1.25,
      marketFactor: 1.15,
      totalFactor: 2.70,
      inflationRate: 15.2,
      notes: "Port Harcourt pricing",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    }
  ]);

  const getGeoZoneName = (geoZoneId: string) => {
    const geoZoneNames: { [key: string]: string } = {
      'LAG': 'Lagos',
      'ABJ': 'Abuja',
      'KAN': 'Kano',
      'PHC': 'Port Harcourt',
      'IBD': 'Ibadan',
      'KAD': 'Kaduna'
    };
    return geoZoneNames[geoZoneId] || geoZoneId;
  };

  const filteredFactors = regionalFactors.filter(factor => {
    const matchesQuarter = selectedQuarter === "all" || factor.quarter === selectedQuarter;
    const matchesCategory = selectedCategory === "all" || factor.materialCategory === selectedCategory;
    const matchesGeoZone = selectedGeoZone === "all" || factor.geoZoneId === selectedGeoZone;
    return matchesQuarter && matchesCategory && matchesGeoZone;
  });

  const getFactorColor = (factor: number) => {
    if (factor >= 3.0) return "text-red-600";
    if (factor >= 2.5) return "text-orange-600";
    if (factor >= 2.0) return "text-yellow-600";
    return "text-green-600";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Regional Price Factors</h2>
          <p className="text-gray-600">Geographic cost factors and adjustments</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Data
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filters & Controls
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="quarter">Quarter</Label>
              <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Quarters</SelectItem>
                  <SelectItem value="2024-Q1">2024 Q1</SelectItem>
                  <SelectItem value="2023-Q4">2023 Q4</SelectItem>
                  <SelectItem value="2023-Q3">2023 Q3</SelectItem>
                  <SelectItem value="2023-Q2">2023 Q2</SelectItem>
                  <SelectItem value="2023-Q1">2023 Q1</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="category">Material Category</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Cement">Cement</SelectItem>
                  <SelectItem value="Steel">Steel</SelectItem>
                  <SelectItem value="Aggregates">Aggregates</SelectItem>
                  <SelectItem value="Blocks">Blocks</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="geoZone">Geo Zone</Label>
              <Select value={selectedGeoZone} onValueChange={setSelectedGeoZone}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Zones</SelectItem>
                  <SelectItem value="LAG">Lagos</SelectItem>
                  <SelectItem value="ABJ">Abuja</SelectItem>
                  <SelectItem value="KAN">Kano</SelectItem>
                  <SelectItem value="PHC">Port Harcourt</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="action">Action</Label>
              <Button className="w-full" size="sm">
                <Settings className="h-4 w-4 mr-2" />
                Update Factors
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Factors</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredFactors.length}</div>
            <p className="text-xs text-muted-foreground">Regional factors</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Factor</CardTitle>
            <Calculator className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {filteredFactors.length > 0 ? 
                (filteredFactors.reduce((sum, factor) => sum + factor.totalFactor, 0) / filteredFactors.length).toFixed(2)
                : 0}
            </div>
            <p className="text-xs text-muted-foreground">Total factor</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Highest Factor</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {filteredFactors.length > 0 ? 
                Math.max(...filteredFactors.map(f => f.totalFactor)).toFixed(2)
                : 0}
            </div>
            <p className="text-xs text-muted-foreground">Lagos Steel</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Geo Zones</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(filteredFactors.map(f => f.geoZoneId)).size}
            </div>
            <p className="text-xs text-muted-foreground">Covered zones</p>
          </CardContent>
        </Card>
      </div>

      {/* Regional Factors Table */}
      <Card>
        <CardHeader>
          <CardTitle>Regional Price Factors</CardTitle>
          <CardDescription>Geographic cost factors and adjustments</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Geo Zone</TableHead>
                <TableHead>Material Category</TableHead>
                <TableHead>Quarter</TableHead>
                <TableHead>Total Factor</TableHead>
                <TableHead>Base Factor</TableHead>
                <TableHead>Transport</TableHead>
                <TableHead>Labor</TableHead>
                <TableHead>Overhead</TableHead>
                <TableHead>Market</TableHead>
                <TableHead>Inflation %</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredFactors.map((factor) => (
                <TableRow key={factor.id}>
                  <TableCell className="font-medium">{getGeoZoneName(factor.geoZoneId)}</TableCell>
                  <TableCell>{factor.materialCategory}</TableCell>
                  <TableCell>{factor.quarter}</TableCell>
                  <TableCell className={`font-medium ${getFactorColor(factor.totalFactor)}`}>
                    {factor.totalFactor.toFixed(3)}
                  </TableCell>
                  <TableCell>{factor.baseFactor.toFixed(3)}</TableCell>
                  <TableCell>{factor.transportFactor.toFixed(3)}</TableCell>
                  <TableCell>{factor.laborFactor.toFixed(3)}</TableCell>
                  <TableCell>{factor.overheadFactor.toFixed(3)}</TableCell>
                  <TableCell>{factor.marketFactor.toFixed(3)}</TableCell>
                  <TableCell>
                    <Badge variant={factor.inflationRate && factor.inflationRate > 15 ? 'destructive' : 'secondary'}>
                      {factor.inflationRate?.toFixed(1)}%
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600 max-w-xs truncate">
                    {factor.notes}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Factor Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Factor Breakdown by Zone</CardTitle>
            <CardDescription>Average factors by geographic zone</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Array.from(new Set(filteredFactors.map(f => f.geoZoneId))).map(zoneId => {
                const zoneFactors = filteredFactors.filter(f => f.geoZoneId === zoneId);
                const avgTotal = zoneFactors.reduce((sum, f) => sum + f.totalFactor, 0) / zoneFactors.length;
                const avgInflation = zoneFactors.reduce((sum, f) => sum + (f.inflationRate || 0), 0) / zoneFactors.length;
                
                return (
                  <div key={zoneId} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="font-medium">{getGeoZoneName(zoneId)}</div>
                      <div className="text-sm text-gray-600">{zoneFactors.length} materials</div>
                    </div>
                    <div className="text-right">
                      <div className={`font-bold ${getFactorColor(avgTotal)}`}>
                        {avgTotal.toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-600">
                        {avgInflation.toFixed(1)}% inflation
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Factor Breakdown by Category</CardTitle>
            <CardDescription>Average factors by material category</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Array.from(new Set(filteredFactors.map(f => f.materialCategory))).map(category => {
                const categoryFactors = filteredFactors.filter(f => f.materialCategory === category);
                const avgTotal = categoryFactors.reduce((sum, f) => sum + f.totalFactor, 0) / categoryFactors.length;
                const avgInflation = categoryFactors.reduce((sum, f) => sum + (f.inflationRate || 0), 0) / categoryFactors.length;
                
                return (
                  <div key={category} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="font-medium">{category}</div>
                      <div className="text-sm text-gray-600">{categoryFactors.length} zones</div>
                    </div>
                    <div className="text-right">
                      <div className={`font-bold ${getFactorColor(avgTotal)}`}>
                        {avgTotal.toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-600">
                        {avgInflation.toFixed(1)}% inflation
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegionalFactors;

